import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { t } from "@/localization";
import { useHelp } from "./Help";
import { helpArticleUrl, helpTopic } from "./helpTopics";
import { colors } from "./theme";

// The (?) next to a setting, web version.
// - Mouse/trackpad: hover, focus or a click on the (?) shows a tooltip with
//   the short explanation. Only a click on the tooltip itself opens the Help
//   Center article in a new tab (at the setting's section), so a click that
//   lands on the (?) next to a link never takes anyone away from the page.
//   From the keyboard, Enter on the (?) shows the tooltip, and Enter again
//   opens the article.
// - Touch screens: tapping opens the help sheet, as in the apps.
// The tooltip is portalled to <body> so scroll containers never clip it.

const WIDTH = 288;
const canHover = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

const HelpTip = ({ topic, size = 16, style }) => {
  const { openHelp } = useHelp();
  const entry = typeof topic === "string" ? helpTopic(topic) : topic;
  const iconRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const timer = useRef(null);
  const tipId = useRef(`help-tip-${Math.random().toString(36).slice(2)}`);

  const place = useCallback(() => {
    const node = iconRef.current;
    if (!node?.getBoundingClientRect) return;
    const r = node.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(WIDTH, vw - 16);
    const left = Math.min(
      Math.max(8, r.left + r.width / 2 - 24),
      vw - width - 8
    );
    const below = vh - r.bottom > 180 || r.top < 180;
    setPos(
      below
        ? { left, top: r.bottom + 8, width }
        : { left, bottom: vh - r.top + 8, width }
    );
  }, []);

  const show = useCallback(
    (delay = 120) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        place();
        setOpen(true);
      }, delay);
    },
    [place]
  );
  const hide = useCallback((delay = 180) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), delay);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Close on Escape, scrolling and resizing.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && hide(0);
    const onScroll = () => hide(0);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, hide]);

  if (!entry) return null;

  const hover = canHover();
  const url = helpArticleUrl(entry.slug, entry.section);
  const title = t(entry.title);
  const summary = t(entry.summary);
  const openArticle = () => {
    hide(0);
    window.open(url, "_blank", "noopener");
  };

  return (
    <>
      <Pressable
        ref={iconRef}
        onPress={(e) => {
          if (!hover) return openHelp(entry, { view: "summary" });
          // A keyboard "click" has detail 0; a mouse click never opens it.
          if (open && e?.nativeEvent?.detail === 0) return openArticle();
          return show(0);
        }}
        onHoverIn={hover ? () => show() : undefined}
        onHoverOut={hover ? () => hide() : undefined}
        onFocus={hover ? () => show(0) : undefined}
        onBlur={hover ? () => hide() : undefined}
        accessibilityRole="button"
        accessibilityLabel={t("Help: {title}", { title })}
        aria-describedby={open ? tipId.current : undefined}
        hitSlop={10}
        style={[tw`ml-1.5 rounded-full`, { cursor: "pointer" }, style]}
      >
        {({ hovered }) => (
          <Ionicons
            name={open ? "help-circle" : "help-circle-outline"}
            size={size}
            color={hovered || open ? colors.slate : colors.subtle}
          />
        )}
      </Pressable>
      {open &&
        pos &&
        typeof document !== "undefined" &&
        createPortal(
          <Pressable
            nativeID={tipId.current}
            role="tooltip"
            onPress={openArticle}
            onHoverIn={() => clearTimeout(timer.current)}
            onHoverOut={() => hide()}
            accessibilityLabel={t(
              "{title}. {summary} Opens the help article in a new tab.",
              { title, summary }
            )}
            style={({ hovered }) => [
              {
                position: "fixed",
                zIndex: 100000,
                left: pos.left,
                width: pos.width,
                top: pos.top,
                bottom: pos.bottom,
                backgroundColor: hovered ? colors.tooltipHover : colors.tooltip,
                borderRadius: 10,
                paddingHorizontal: 14,
                paddingVertical: 12,
                boxShadow: "0 8px 24px rgba(17,24,39,0.22)",
                cursor: "pointer",
              },
            ]}
          >
            <View style={tw`flex-row items-center mb-1`}>
              <Text
                style={tw`flex-1 text-[13px] leading-[18px] font-semibold text-white`}
              >
                {title}
              </Text>
              <Ionicons
                name="open-outline"
                size={13}
                color={colors.tooltipLink}
                style={tw`ml-2`}
              />
            </View>
            <Text style={tw`text-[13px] leading-[19px] text-[#E5E7EB]`}>
              {summary}
            </Text>
          </Pressable>,
          document.body
        )}
    </>
  );
};

export default HelpTip;
